import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BaseDirective, LoggerService, ObjectUtils } from '@egym/ngx-adm-int';
import { BehaviorSubject } from 'rxjs';
import {
  GymDay,
  ExceptionalOpeningHoursV2,
  OpeningTimeRange,
  OpeningTimeSentinel,
} from 'src/app/model/opening-hours';
import { Gym } from 'src/app/model/gym';
import { StudioType } from 'src/app/model/studio-type';
import { ValidateAddressDialogComponent } from '../validate-address-dialog/validate-address-dialog.component';
import { Options } from 'src/app/model/options';
import { ExternalBookingInfo } from '../../../model/external-booking-info';
import { CreditsConfig } from '../../../model/credits/credits-config';
import {
  dayValuesEqual,
  OPENING_HOURS_INFO_TEXT_PATTERN,
  SCHEDULABLE_DAYS,
} from 'src/app/util/opening-hour-util';

@Component({
  selector: 'gymfinder-gym-details',
  templateUrl: './gym-details.component.html',
  styleUrls: ['./gym-details.component.scss'],
  standalone: false,
})
export class GymDetailsComponent extends BaseDirective<Gym> implements OnChanges {
  @Input()
  loading: boolean;

  @Input()
  gym: Gym;

  @Input()
  gymAlias?: string;

  @Input()
  studioTypes: StudioType[];

  @Input()
  editMode: boolean;

  @Input()
  gymSavedSubject: BehaviorSubject<boolean>;

  @Input()
  displayGymlibBackOfficeLink: boolean;

  @Input()
  creditsFeatureFlag: boolean;

  openingDays = SCHEDULABLE_DAYS;

  sameForAllDays = false;

  openingHoursInfoMaxLength: number = 300;

  openingHoursInfoTextPattern = OPENING_HOURS_INFO_TEXT_PATTERN;

  get openingHoursInfoLength(): number {
    return this.gym.openingHoursV2?.additionalInfo?.length ?? 0;
  }

  constructor(
    snackBar: MatSnackBar,
    private dialog: MatDialog,
    private logger: LoggerService
  ) {
    super(snackBar);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['gym'] && this.gym?.openingHoursV2) {
      this.sameForAllDays = this.detectSameForAllDays();
    }
  }

  onSameForAllDaysChange(checked: boolean): void {
    this.sameForAllDays = checked;
    if (checked && this.gym?.openingHoursV2) {
      // Broadcast monday's value to all other weekdays
      const mondayValue = this.gym.openingHoursV2.monday;
      for (const day of SCHEDULABLE_DAYS) {
        if (day !== 'monday') {
          this.gym.openingHoursV2[day] = Array.isArray(mondayValue)
            ? mondayValue.map(r => ({ start: { ...r.start }, end: { ...r.end } }))
            : mondayValue;
        }
      }
    }
  }

  onAllDaysValueChange(value: OpeningTimeRange[] | OpeningTimeSentinel): void {
    for (const day of SCHEDULABLE_DAYS) {
      this.gym.openingHoursV2[day] = Array.isArray(value)
        ? value.map(r => ({ start: { ...r.start }, end: { ...r.end } }))
        : value;
    }
    this.logger.info('new opening hours V2: ', this.gym.openingHoursV2);
  }

  private detectSameForAllDays(): boolean {
    const oh = this.gym.openingHoursV2;
    if (!oh) return false;
    const ref = oh.monday;
    return SCHEDULABLE_DAYS.every(d => dayValuesEqual(oh[d], ref));
  }

  getDayValue(
    day: GymDay
  ): OpeningTimeRange[] | OpeningTimeSentinel | undefined {
    return this.gym.openingHoursV2?.[day];
  }

  onDayValueChange(
    value: OpeningTimeRange[] | OpeningTimeSentinel,
    day: GymDay
  ) {
    this.gym.openingHoursV2[day] = value;
    this.logger.info('new opening hours V2: ', this.gym.openingHoursV2);
  }

  onAdditionalInfoTextChange(value: string) {
    this.gym.openingHoursV2.additionalInfo = value || null;
  }

  editAddress() {
    const dialogRef = this.dialog.open(ValidateAddressDialogComponent, {
      data: ObjectUtils.deepCopy(this.gym.address),
    });
    dialogRef.afterClosed().subscribe(address => {
      if (address) {
        this.gym.address = address;
      }
    });
  }

  public onOptionsChange(newOptions: Options) {
    this.logger.info('New Options received: ', newOptions);
    this.gym.options = newOptions;
  }

  addException() {
    if (!this.gym.openingHoursV2) return;
    if (!this.gym.openingHoursV2.exceptions) {
      this.gym.openingHoursV2.exceptions = [];
    }
    this.gym.openingHoursV2.exceptions.push({
      period: { startDate: '', endDate: '' },
      time: [{ start: { hour: 8, minute: 0 }, end: { hour: 22, minute: 0 } }],
    });
  }

  onExceptionChange(newValue: ExceptionalOpeningHoursV2, index: number) {
    if (!this.gym.openingHoursV2?.exceptions?.[index]) return;
    this.gym.openingHoursV2.exceptions[index] = newValue;
  }

  onRemoveException(index: number) {
    this.gym.openingHoursV2?.exceptions?.splice(index, 1);
  }

  onExternalBookingInfoChanged(newExternalBookingInfo: ExternalBookingInfo) {
    this.logger.info(
      'New external booking info received: ',
      newExternalBookingInfo
    );
    this.gym.externalBookingInfo = newExternalBookingInfo;
  }

  public onCreditsConfigChange(newCreditsConfig: CreditsConfig) {
    this.logger.info('New credits config received: ', newCreditsConfig);
    this.gym.creditsConfig = newCreditsConfig;
  }
}
